import { useRef, useEffect, useCallback } from 'react';

interface UseRibbonScrollProps {
  value?: any;
  items: IRibbonItem[];
}

interface UseRibbonScrollReturn {
  ribbonContainerRef: React.RefObject<HTMLDivElement>;
  tabRefs: React.MutableRefObject<Record<string, HTMLElement>>;
  setTabRef: (itemValue: any) => (ref: HTMLElement | null) => void;
}

/**
 * Custom hook to handle horizontal scrolling of ribbon tabs when the selected value changes.
 * Automatically scrolls the selected tab into view when it's not fully visible.
 */
export function useRibbonScroll({
  value,
  items: _items,
}: UseRibbonScrollProps): UseRibbonScrollReturn {
  const ribbonContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLElement>>({});
  const previousValueRef = useRef<any>(value);

  const setTabRef = useCallback(
    (itemValue: any) => (ref: HTMLElement | null) => {
      if (ref) {
        tabRefs.current[itemValue] = ref;
      } else {
        delete tabRefs.current[itemValue];
      }
    },
    []
  );

  // Scroll the selected tab into view when value changes
  useEffect(() => {
    // Only scroll if value actually changed (not on initial mount)
    if (value && value !== previousValueRef.current && tabRefs.current[value]) {
      const selectedTab = tabRefs.current[value];
      const container = ribbonContainerRef.current;

      if (selectedTab && container) {
        const containerRect = container.getBoundingClientRect();
        const tabRect = selectedTab.getBoundingClientRect();

        // Check if tab is fully visible with some margin
        const margin = 20; // Add some margin for better UX
        const isFullyVisible =
          tabRect.left >= containerRect.left + margin &&
          tabRect.right <= containerRect.right - margin;

        if (!isFullyVisible) {
          const tabCenter = tabRect.left + tabRect.width / 2;
          const containerCenter = containerRect.left + container.clientWidth / 2;
          const delta = tabCenter - containerCenter;
          const scrollLeft = container.scrollLeft + delta;
          const maxScrollLeft = container.scrollWidth - container.clientWidth;
          const clampedScrollLeft = Math.max(0, Math.min(scrollLeft, maxScrollLeft));

          const prefersReducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
          ).matches;

          container.scrollTo({
            left: clampedScrollLeft,
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
          });
        }
      }
    }
    previousValueRef.current = value;
  }, [value]);

  return {
    ribbonContainerRef,
    tabRefs,
    setTabRef,
  };
}
